import React, { createContext, useContext } from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { ChartsTooltipContainer, useItemTooltip } from "@mui/x-charts/ChartsTooltip";
import { formatAxisValue } from "visual-components/util/graphs/formatAxisValue";
import { getChartColor } from "./chartColors";

// Context to pass tooltip data (category totals, labels, and series info)
const TooltipDataContext = createContext({
  categoryTotals: [],
  categoryLabels: [],
  seriesData: [],
});

/**
 * Custom tooltip content that shows category label, total in header, and all brand values
 * @returns {import("react").ReactElement}
 */
function CustomTooltipContent() {
  const tooltipData = useItemTooltip();
  const { categoryTotals, categoryLabels, seriesData } = useContext(TooltipDataContext);

  if (!tooltipData) {
    return <></>;
  }

  const { identifier } = tooltipData;
  const dataIndex = identifier.dataIndex;
  const total = categoryTotals[dataIndex] || 0;
  const categoryLabel = categoryLabels[dataIndex] || "";

  return (
    <Paper elevation={3} sx={{ p: 1.5, minWidth: 180 }}>
      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
        {categoryLabel}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
        Total: {total.toLocaleString()} clicks
      </Typography>
      <Stack spacing={0.5}>
        {seriesData.map((series, index) => {
          const value = series.data[dataIndex] || 0;
          if (value === 0) return null;

          return (
            <Stack key={index} direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: series.color,
                  borderRadius: "2px",
                  flexShrink: 0,
                }}
              />
              <Typography variant="body2">
                {series.label}: {value.toLocaleString()} clicks
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </Paper>
  );
}

/**
 * Custom tooltip wrapper using ChartsTooltipContainer for positioning
 * @returns {import("react").ReactElement}
 */
function CustomTooltip() {
  return (
    <ChartsTooltipContainer trigger="item">
      <CustomTooltipContent />
    </ChartsTooltipContainer>
  );
}

/**
 * Social Distribution Graph Component using MUI X Charts
 * Displays a stacked bar chart showing social actions by platform
 * @param {Object} props
 * @param {Array} props.socialItems - Array of social items with name, title, and data
 * @param {Array} props.categories - Array of platform categories
 * @param {Object} props.hideMap - Map of hidden items
 * @returns {import("react").ReactElement}
 */
export default function SocialDistributionGraph(props) {
  const { socialItems = [], categories = [], hideMap = {} } = props;

  // Filter selected social items while preserving original index for consistent colors
  const selectedItems = socialItems
    .map((item, originalIndex) => ({ ...item, originalIndex }))
    .filter((item) => !hideMap[item.name]);

  if (selectedItems.length === 0 || categories.length === 0) {
    return <></>;
  }

  // Filter categories that have data in any selected item
  const categoriesWithData = categories
    .map((category, idx) => ({ name: category, index: idx }))
    .filter(({ index }) => selectedItems.some((item) => item.data[index] > 0));

  if (categoriesWithData.length === 0) {
    return <></>;
  }

  // Calculate totals for each category (sum across all selected brands)
  const categoryTotals = categoriesWithData.map(({ index }) =>
    selectedItems.reduce((sum, item) => sum + (item.data[index] || 0), 0),
  );

  // Build series data for each social item using centralized colors
  const series = selectedItems.map((item) => ({
    data: categoriesWithData.map(({ index }) => item.data[index] || 0),
    label: item.title,
    color: getChartColor(item.originalIndex),
    stack: "social",
    valueFormatter: (value) => `${value?.toLocaleString()} clicks`,
  }));

  const xAxisCategories = categoriesWithData.map(({ name }) => name);

  // Context value with totals, labels, and series data for the tooltip
  const tooltipContextValue = {
    categoryTotals,
    categoryLabels: xAxisCategories,
    seriesData: series,
  };

  return (
    <TooltipDataContext.Provider value={tooltipContextValue}>
      <Box sx={{ height: 300, width: "100%" }}>
        <BarChart
          xAxis={[
            {
              scaleType: "band",
              data: xAxisCategories,
              tickLabelStyle: {
                fontSize: 11,
              },
            },
          ]}
          yAxis={[
            {
              disableLine: true,
              valueFormatter: formatAxisValue,
            },
          ]}
          series={series}
          height={300}
          grid={{ horizontal: true }}
          hideLegend
          slots={{
            tooltip: CustomTooltip,
          }}
          slotProps={{
            bar: {
              clipPath: "none",
            },
            legend: {
              sx: {
                fontSize: 16,
              },
            },
          }}
        />
      </Box>
    </TooltipDataContext.Provider>
  );
}
